import React, { useEffect, useState, useRef } from 'react';
import IkIslemleriAPI, { MemIzınBilgiDurumListRequest, IsmIzınBilgiDurumListRequest } from '../../../apis/IkIslemleriAPI';
import TableComponent from "../../../components/OB/TableComponent";


interface YillikIzinHakkedisleriProps {
    qtSicilId: number;
    personelTipi: string;
    onTotalsCalculated?: (totals: { hakedis: number; kullanilan: number; kalan: number }) => void;
}

const YillikIzinHakkedisleri: React.FC<YillikIzinHakkedisleriProps> = ({ qtSicilId, personelTipi, onTotalsCalculated }) => {
    const [izinHakkedisList, setIzinHakkedisList] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [yearSort, setYearSort] = useState<'asc' | 'desc' | null>(null);

    // API istek kontrolü - aynı anda birden fazla istek engelleme
    const isLoadingRef = useRef(false);
    // Son yüklenen sicilId - gereksiz tekrar yüklemeyi önle
    const lastLoadedSicilRef = useRef<number | null>(null);

    // Sayı formatlayıcı (Türkçe, 2 ondalık)
    const formatNumber = (value: number): string => {
        return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    };

    const sortedData = React.useMemo(() => {
        if (!yearSort) return izinHakkedisList;
        return [...izinHakkedisList].sort((a, b) =>
            yearSort === "asc"
                ? Number(a.qtYil) - Number(b.qtYil)
                : Number(b.qtYil) - Number(a.qtYil)
        );
    }, [izinHakkedisList, yearSort]);

    const renderYearSortIcon = () => {
        if (!yearSort) {
            return <span style={{ marginLeft: 6, opacity: 0.3 }}>⇅</span>;
        }

        return yearSort === "asc"
            ? <span style={{ marginLeft: 6 }}>▲</span>
            : <span style={{ marginLeft: 6 }}>▼</span>;
    };

    const columns = [
        {
            header: (
                <div
                    onClick={() =>
                        setYearSort(prev => (prev === null ? 'asc' : (prev === "asc" ? "desc" : "asc")))
                    }
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        userSelect: "none"
                    }}
                >
                    Yıl
                    {renderYearSortIcon()}
                </div>
            ) as any,
            field: "qtYil",
            className: "text-center"
        },
        { header: "Hak Edilen", field: "hakedis", className: "text-right", render: (row: any) => (row.hakedis === null || row.hakedis === undefined) ? '0,00' : formatNumber(Number(row.hakedis)) },
        { header: "Kullanılan", field: "kullanilan", className: "text-right", render: (row: any) => (row.kullanilan === null || row.kullanilan === undefined) ? '0,00' : formatNumber(Number(row.kullanilan)) },
        { header: "Kalan", field: "kalan", className: "text-right", render: (row: any) => (row.kalan === null || row.kalan === undefined) ? '0,00' : formatNumber(Number(row.kalan)) },
    ];

    useEffect(() => {
        const fetchIzinHakkedisleri = async () => {
            // Rate limiting kontrolü - aynı istek devam ediyorsa veya aynı sicil zaten yüklendiyse çık
            if (isLoadingRef.current) {
                return;
            }
            if (lastLoadedSicilRef.current === qtSicilId && izinHakkedisList.length > 0) {
                return;
            }

            setError(null);
            if (!qtSicilId) {
                setError("Sicil ID mevcut değil.");
                setLoading(false);
                return;
            }
            try {
                isLoadingRef.current = true;
                setLoading(true);
                let response;
                if (personelTipi === 'I' || personelTipi === 'K' || personelTipi === 'T') {
                    const request: IsmIzınBilgiDurumListRequest = { qtSicilId };
                    response = await IkIslemleriAPI.ismIzınBilgiDurumList(request);
                } else {
                    const request: MemIzınBilgiDurumListRequest = { qtSicilId };
                    response = await IkIslemleriAPI.memIzınBilgiDurumList(request);
                }

                if (response && response.data) {
                    setIzinHakkedisList(response.data);
                    lastLoadedSicilRef.current = qtSicilId;
                } else {
                    setIzinHakkedisList([]);
                }
            } catch (err: any) {
                setIzinHakkedisList([]);
                setError("Veri çekilirken bir hata oluştu: " + (err?.message ?? err));
                console.error("Yıllık İzin Hakkediş Listesi Hatası:", err);
            } finally {
                isLoadingRef.current = false;
                setLoading(false);
            }
        };
        fetchIzinHakkedisleri();
    }, [qtSicilId, personelTipi]);

    // Toplamları hesapla
    const totals = izinHakkedisList.reduce(
        (acc, row) => {
            const hakedis = Number(row.hakedis ?? 0);
            const kullanilan = Number(row.kullanilan ?? 0);
            const kalan = Number(row.kalan ?? 0);
            acc.hakedis += isNaN(hakedis) ? 0 : hakedis;
            acc.kullanilan += isNaN(kullanilan) ? 0 : kullanilan;
            acc.kalan += isNaN(kalan) ? 0 : kalan;
            return acc;
        },
        { hakedis: 0, kullanilan: 0, kalan: 0 }
    );

    useEffect(() => {
        if (!loading && !error && onTotalsCalculated) {
            onTotalsCalculated(totals);
        }
    }, [loading, error, totals, onTotalsCalculated]);

    return (
        <TableComponent
            columns={columns}
            data={sortedData}
            recordsPerPage={12}
        />
    );
};

export default YillikIzinHakkedisleri;
